import { isAnswerDefect, type InspectionQuestion } from '@sitewise/shared';
import { Badge, Label } from '@sitewise/ui';
import { AlertTriangle } from 'lucide-react';

import type { InspectionAnswerSummary } from '../../../api/inspections.js';

import { yesNoLabel } from './form-helpers.js';

interface ChecklistRowProps {
  question: Extract<InspectionQuestion, { kind: 'yes_no' }>;
  answer: InspectionAnswerSummary | null;
  readOnly: boolean;
  onAnswer: (value: 'yes' | 'no' | 'na', notes: string) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  onNotesBlur: () => void;
}

function ChecklistRow({
  question,
  answer,
  readOnly,
  onAnswer,
  notes,
  onNotesChange,
  onNotesBlur,
}: ChecklistRowProps) {
  const value = answer?.value?.kind === 'yes_no' ? answer.value.value : null;
  const isDefect =
    answer?.value && answer.value.kind === 'yes_no'
      ? isAnswerDefect(question, answer.value)
      : false;
  const options: Array<'na' | 'yes' | 'no'> = question.allowNa
    ? ['na', 'yes', 'no']
    : ['yes', 'no'];

  return (
    <tr className="border-b border-border align-top">
      <td className="px-3 py-2 text-sm">
        <div className="flex items-start gap-2">
          <span>{question.label}</span>
          {isDefect ? (
            <Badge variant="danger" className="shrink-0 text-[10px]">
              <AlertTriangle className="h-3 w-3" />
              DEFECT
            </Badge>
          ) : null}
        </div>
      </td>
      {options.map((opt) => (
        <td key={opt} className="px-2 py-2 text-center">
          {readOnly ? (
            <span className="text-sm">{value === opt ? '●' : ''}</span>
          ) : (
            <input
              type="radio"
              name={`q-${question.id}`}
              checked={value === opt}
              onChange={() => onAnswer(opt, notes)}
              aria-label={`${question.label} — ${yesNoLabel(opt)}`}
              className="h-4 w-4"
            />
          )}
        </td>
      ))}
      {question.allowNa ? null : <td className="px-2 py-2" aria-hidden />}
      <td className="px-3 py-2">
        {readOnly && !notes ? (
          <span className="text-xs text-fg-muted">—</span>
        ) : (
          <textarea
            rows={2}
            value={notes}
            onChange={(e) => onNotesChange(e.target.value)}
            onBlur={onNotesBlur}
            disabled={readOnly}
            placeholder="Comments"
            className="w-full min-w-[10rem] rounded border border-border bg-surface px-2 py-1 text-xs"
          />
        )}
      </td>
    </tr>
  );
}

interface Props {
  title: string;
  questions: InspectionQuestion[];
  answersById: Map<string, InspectionAnswerSummary>;
  readOnly: boolean;
  localNotes: Record<string, string>;
  onNotesChange: (questionId: string, notes: string) => void;
  onCommit: (
    question: Extract<InspectionQuestion, { kind: 'yes_no' }>,
    value: 'yes' | 'no' | 'na',
    notes: string,
  ) => void;
}

export function ChecklistTableSection({
  title,
  questions,
  answersById,
  readOnly,
  localNotes,
  onNotesChange,
  onCommit,
}: Props) {
  const checklistQuestions = questions.filter(
    (q): q is Extract<InspectionQuestion, { kind: 'yes_no' }> => q.kind === 'yes_no',
  );
  if (checklistQuestions.length === 0) return null;
  const showNa = checklistQuestions.some((q) => q.allowNa);

  return (
    <div className="overflow-x-auto rounded border border-border">
      <div className="border-b border-border bg-surface-secondary px-3 py-2">
        <Label className="text-sm font-semibold">{title}</Label>
      </div>
      <table className="w-full min-w-[720px] border-collapse text-left">
        <thead>
          <tr className="border-b border-border bg-surface-secondary text-xs uppercase tracking-wide text-fg-muted">
            <th className="px-3 py-2 font-medium">Question</th>
            {showNa ? <th className="px-2 py-2 text-center font-medium">N/A</th> : null}
            <th className="px-2 py-2 text-center font-medium">Yes</th>
            <th className="px-2 py-2 text-center font-medium">No</th>
            {!showNa ? <th className="px-2 py-2" /> : null}
            <th className="px-3 py-2 font-medium">Comments</th>
          </tr>
        </thead>
        <tbody>
          {checklistQuestions.map((q) => {
            const answer = answersById.get(q.id) ?? null;
            const notes = localNotes[q.id] ?? answer?.notes ?? '';
            return (
              <ChecklistRow
                key={q.id}
                question={q}
                answer={answer}
                readOnly={readOnly}
                notes={notes}
                onNotesChange={(next) => onNotesChange(q.id, next)}
                onNotesBlur={() => {
                  if (answer?.value?.kind === 'yes_no' && answer.value.value) {
                    onCommit(q, answer.value.value, notes);
                  }
                }}
                onAnswer={(value, rowNotes) => onCommit(q, value, rowNotes)}
              />
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
